import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthToken } from "../../../utils/getAuthToken";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Debouncing for API calls to prevent spam
const debounceMap = new Map();
const DEBOUNCE_TIME = 5000; // 5 seconds

const shouldDebounce = (key) => {
  const now = Date.now();
  const lastCall = debounceMap.get(key);

  if (lastCall && (now - lastCall) < DEBOUNCE_TIME) {
    return true; // Should debounce (skip this call)
  }

  debounceMap.set(key, now);
  return false; // Don't debounce (allow this call)
};

// Clears the module-level debounce map — call on logout so the next
// account's first fetch is never silently skipped as "too soon".
export const __resetContactGroupsCaches = () => {
  debounceMap.clear();
};

// Async thunk for fetching all contact groups
export const fetchContactGroups = createAsyncThunk(
  "contactGroups/fetchContactGroups",
  async (_, { rejectWithValue }) => {
    // Debounce check
    if (shouldDebounce("fetchContactGroups")) {
      return rejectWithValue("Debounced");
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/contact-groups`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch contact groups");
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for creating a new contact group
export const createContactGroup = createAsyncThunk(
  "contactGroups/createContactGroup",
  async (groupData, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/contact-groups/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(groupData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create contact group");
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for updating a contact group
export const updateContactGroup = createAsyncThunk(
  "contactGroups/updateContactGroup",
  async ({ groupId, updateData }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/contact-groups/${groupId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update contact group");
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for deleting a contact group
export const deleteContactGroup = createAsyncThunk(
  "contactGroups/deleteContactGroup",
  async (groupId, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/contact-groups/${groupId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete contact group");
      }

      return groupId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for adding contacts to a group
export const addContactsToGroup = createAsyncThunk(
  "contactGroups/addContactsToGroup",
  async ({ groupId, contactIds }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/contact-groups/add-contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ groupId, contactIds }),
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || "Failed to add contacts to group");
      }

      // Return detailed information about the operation
      return { 
        groupId, 
        updatedGroup: data.data.group,
        addedContacts: data.data.added,
        alreadyInGroup: data.data.alreadyInGroup,
        totalRequested: data.data.totalRequested,
        totalAdded: data.data.totalAdded,
        totalAlreadyInGroup: data.data.totalAlreadyInGroup,
        message: data.message
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for removing contacts from a group
export const removeContactsFromGroup = createAsyncThunk(
  "contactGroups/removeContactsFromGroup",
  async ({ groupId, contactIds }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/contact-groups/remove-contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ groupId, contactIds }),
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || "Failed to remove contacts from group");
      }

      // Return detailed information about the operation
      return { 
        groupId, 
        updatedGroup: data.data.group,
        removedContacts: data.data.removed,
        notInGroup: data.data.notInGroup,
        totalRequested: data.data.totalRequested,
        totalRemoved: data.data.totalRemoved,
        totalNotInGroup: data.data.totalNotInGroup,
        message: data.message
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for importing contacts from CSV
export const importContactsFromCSV = createAsyncThunk(
  "contactGroups/importContactsFromCSV",
  async ({ groupId, csvFile, contacts }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      let response;
      
      if (contacts && Array.isArray(contacts)) {
        // Send parsed contacts directly
        response = await fetch(
          `${SERVER_DOMAIN}/api/contact-groups/import-csv`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              groupId,
              contacts
            }),
          }
        );
      } else if (csvFile) {
        // Send CSV file for server-side parsing
        const formData = new FormData();
        formData.append('csvFile', csvFile);
        formData.append('groupId', groupId);

        response = await fetch(
          `${SERVER_DOMAIN}/api/contact-groups/import-csv`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );
      } else {
        throw new Error("Either contacts data or CSV file is required");
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to import contacts from CSV");
      }

      const data = await response.json();
      return { groupId, importResult: data.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  groups: [],
  loading: false,
  error: null,
  selectedGroup: null,
  createModalOpen: false,
  importModalOpen: false,
  selectedGroupForImport: null,
};

const contactGroupsSlice = createSlice({
  name: "contactGroups",
  initialState,
  reducers: {
    setSelectedGroup: (state, action) => {
      state.selectedGroup = action.payload;
    },
    setCreateModalOpen: (state, action) => {
      state.createModalOpen = action.payload;
    },
    setImportModalOpen: (state, action) => {
      state.importModalOpen = action.payload;
    },
    setSelectedGroupForImport: (state, action) => {
      state.selectedGroupForImport = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch contact groups
      .addCase(fetchContactGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContactGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload;
        state.error = null;
      })
      .addCase(fetchContactGroups.rejected, (state, action) => {
        state.loading = false;
        if (action.payload !== "Debounced") {
          state.error = action.payload;
        }
      })
      // Create contact group
      .addCase(createContactGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createContactGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.groups.unshift(action.payload);
        state.createModalOpen = false;
        state.error = null;
      })
      .addCase(createContactGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update contact group
      .addCase(updateContactGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContactGroup.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.groups.findIndex(group => group._id === action.payload._id);
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateContactGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete contact group
      .addCase(deleteContactGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteContactGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = state.groups.filter(group => group._id !== action.payload);
        state.error = null;
      })
      .addCase(deleteContactGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add contacts to group
      .addCase(addContactsToGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addContactsToGroup.fulfilled, (state, action) => {
        state.loading = false;
        const { groupId, updatedGroup } = action.payload;
        const index = state.groups.findIndex(group => group._id === groupId);
        if (index !== -1) {
          state.groups[index] = updatedGroup;
        }
        state.error = null;
      })
      .addCase(addContactsToGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Remove contacts from group
      .addCase(removeContactsFromGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeContactsFromGroup.fulfilled, (state, action) => {
        state.loading = false;
        const { groupId, updatedGroup } = action.payload;
        const index = state.groups.findIndex(group => group._id === groupId);
        if (index !== -1) {
          state.groups[index] = updatedGroup;
        }
        state.error = null;
      })
      .addCase(removeContactsFromGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Import contacts from CSV
      .addCase(importContactsFromCSV.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(importContactsFromCSV.fulfilled, (state, action) => {
        state.loading = false;
        const { groupId, importResult } = action.payload;
        const index = state.groups.findIndex(group => group._id === groupId);
        if (index !== -1) {
          state.groups[index] = importResult.group;
        }
        state.importModalOpen = false;
        state.error = null;
      })
      .addCase(importContactsFromCSV.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedGroup,
  setCreateModalOpen,
  setImportModalOpen,
  setSelectedGroupForImport,
  clearError,
} = contactGroupsSlice.actions;

export default contactGroupsSlice.reducer;
